/**
 * TeamGithubConnection: the team's installation of the TrainLoop GitHub
 * App (TRA-1108) on the staff Connections page. The customer installs
 * the App from Settings > GitHub App and sends TrainLoop the installation
 * id; this is where staff record it. The section renders the same
 * status the customer page does (installed, not connected, gone from
 * GitHub, unknown), so both sides read one answer. The Evals config page
 * reads the same status to offer the installation's repositories to a
 * benchmark.
 *
 * Not connected shows the form over
 * POST /api/internal/teams/:id/connections with kind=github. The api
 * mints a token for the installation before writing the row, so a wrong
 * id, an uninstalled App or an installation that omits the repository is
 * a 400 here and not a failed job tonight; the message lands in the form
 * footer. Every other state shows the linked row with a Remove action,
 * so a dead installation can be unlinked and a new one recorded.
 *
 * Cloud GitHub with the platform App is the default. A GitHub Enterprise
 * Server host needs the App registered on that instance, so its id and
 * private key appear once the API base URL leaves api.github.com.
 */

import { useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ExternalLink, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { getApiErrorDetail } from '@/api/client'
import {
  getListTeamConnectionsApiInternalTeamsTeamIdConnectionsGetQueryKey,
  useCreateTeamConnectionApiInternalTeamsTeamIdConnectionsPost,
  useDeleteTeamConnectionApiInternalConnectionsConnectionIdDelete,
  useGetTeamGithubAppApiInternalTeamsTeamIdGithubAppGet,
} from '@/api/generated/internal/internal'
import type {
  ConnectionAdminItem,
  GithubAppStatus,
  ImpersonatableTeam,
} from '@/api/generated/model'
import { RowAction } from '@/components/settings/RowAction'
import { SettingsFormFooter } from '@/components/settings/SettingsFormFooter'
import { SettingsList, SettingsListRow } from '@/components/settings/SettingsList'
import { SettingsSection } from '@/components/settings/SettingsSection'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { GithubAppStatusBadge } from '@/components/shared/GithubAppStatusBadge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'

import { GITHUB_CLOUD_API_BASE_URL } from './githubConnection'

const EXTERNAL_LINK =
  'inline-flex items-center gap-1 text-foreground underline underline-offset-4 hover:text-primary'

interface TeamGithubConnectionProps {
  team: ImpersonatableTeam
  /** The linked row, for Remove; undefined when none is linked. */
  connection: ConnectionAdminItem | undefined
}

export function TeamGithubConnection({
  team,
  connection,
}: TeamGithubConnectionProps): React.JSX.Element {
  // One GitHub read per load, like Settings > GitHub App; a failure is a
  // status in the body, so a retry would only repeat the same answer.
  const query = useGetTeamGithubAppApiInternalTeamsTeamIdGithubAppGet(team.id, {
    query: { retry: false },
  })
  const status = query.data?.status === 200 ? query.data.data : undefined
  // After a link or unlink the installation is read again.
  const reload = (): void => {
    void query.refetch()
  }
  return (
    <SettingsSection
      title="GitHub"
      description="This installation of the TrainLoop GitHub App signs every read of the team's repository. The customer installs it from Settings, GitHub App, and sends TrainLoop the installation id."
    >
      {query.isLoading ? (
        <Skeleton className="h-14 w-full" />
      ) : query.isError || status === undefined ? (
        <p className="text-sm text-destructive" role="alert">
          Failed to load the installation.
        </p>
      ) : status.status === 'not_connected' ? (
        <GithubConnectionForm team={team} installUrl={status.install_url} onLinked={reload} />
      ) : (
        <GithubConnectionRow
          team={team}
          status={status}
          connection={connection}
          onRemoved={reload}
        />
      )}
    </SettingsSection>
  )
}

function useInvalidateConnections(teamId: string): () => void {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({
      queryKey: getListTeamConnectionsApiInternalTeamsTeamIdConnectionsGetQueryKey(teamId),
    })
  }
}

/** "Installation 42 · 3 repositories · App trainloop-evals", plus the host off cloud GitHub. */
function connectionMeta(status: GithubAppStatus, connection: ConnectionAdminItem | undefined): string {
  const parts: string[] = []
  if (status.installation !== null) {
    parts.push(`Installation ${status.installation.installation_id.toString()}`)
    const count = status.installation.repository_count
    parts.push(`${count.toString()} ${count === 1 ? 'repository' : 'repositories'}`)
  } else if (connection !== undefined) {
    parts.push(`Installation ${connection.identifier}`)
  }
  // GitHub's own name for the App, read on this request. Absent only when
  // its page could not be read, which the status itself already reports.
  if (status.app_slug !== null) {
    parts.push(`App ${status.app_slug}`)
  }
  if (connection?.api_base_url != null && connection.api_base_url !== GITHUB_CLOUD_API_BASE_URL) {
    parts.push(connection.api_base_url)
  }
  return parts.join(' · ')
}

function GithubConnectionRow({
  team,
  status,
  connection,
  onRemoved,
}: {
  team: ImpersonatableTeam
  status: GithubAppStatus
  connection: ConnectionAdminItem | undefined
  onRemoved: () => void
}): React.JSX.Element {
  const invalidate = useInvalidateConnections(team.id)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [removeError, setRemoveError] = useState<string | null>(null)
  const remove = useDeleteTeamConnectionApiInternalConnectionsConnectionIdDelete({
    mutation: {
      onSuccess: () => {
        invalidate()
        onRemoved()
        setConfirmOpen(false)
        toast.success('GitHub connection removed')
      },
      onError: (error: unknown) => {
        setRemoveError(getApiErrorDetail(error) ?? 'The connection was not removed')
      },
    },
  })
  const name =
    status.installation !== null ? status.installation.account : (connection?.display_name ?? 'GitHub')
  return (
    <>
      <SettingsList>
        <SettingsListRow>
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="flex flex-wrap items-center gap-2">
              <span className="truncate text-sm text-foreground">{name}</span>
              <GithubAppStatusBadge status={status.status} />
            </span>
            <span className="truncate font-mono text-xs text-muted-foreground">
              {connectionMeta(status, connection)}
            </span>
            {status.installation !== null && (
              <a
                href={status.installation.html_url}
                target="_blank"
                rel="noreferrer"
                className={`${EXTERNAL_LINK} text-xs`}
              >
                Installation page on GitHub
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </a>
            )}
          </span>
          {connection !== undefined && (
            <RowAction
              label={`Remove ${connection.display_name}`}
              icon={Trash2}
              destructive
              onClick={() => {
                setRemoveError(null)
                setConfirmOpen(true)
              }}
            />
          )}
        </SettingsListRow>
      </SettingsList>
      {status.detail !== null && (
        <p className="mt-3 max-w-xl text-sm text-destructive" role="alert">
          {status.detail}
        </p>
      )}
      {connection !== undefined && (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title="Remove GitHub connection"
          description={
            <>
              Remove <strong>{connection.display_name}</strong>? Every nightly that reads this
              team&apos;s repository fails until a connection is recorded again.
            </>
          }
          confirmLabel="Remove"
          pendingLabel="Removing..."
          destructive
          isPending={remove.isPending}
          error={removeError}
          onConfirm={() => {
            remove.mutate({ connectionId: connection.id })
          }}
        />
      )}
    </>
  )
}

interface ConnectionFormState {
  display_name: string
  installation_id: string
  api_base_url: string
  app_id: string
  app_private_key: string
}

function emptyForm(team: ImpersonatableTeam): ConnectionFormState {
  return {
    display_name: `${team.name} GitHub`,
    installation_id: '',
    api_base_url: GITHUB_CLOUD_API_BASE_URL,
    app_id: '',
    app_private_key: '',
  }
}

function isEnterpriseHost(form: ConnectionFormState): boolean {
  return form.api_base_url.trim() !== GITHUB_CLOUD_API_BASE_URL
}

/** The first thing wrong with the form, or null when it can be sent. */
function validateConnection(form: ConnectionFormState): string | null {
  if (form.display_name.trim() === '') return 'Give the connection a name.'
  if (!/^[1-9]\d*$/.test(form.installation_id.trim())) {
    return 'The installation id is the number at the end of the installation URL.'
  }
  if (!/^https:\/\/./i.test(form.api_base_url.trim())) {
    return 'The API base URL must start with https:// and include a host.'
  }
  if (isEnterpriseHost(form) && (form.app_id.trim() === '' || form.app_private_key.trim() === '')) {
    return 'A GitHub Enterprise Server host needs the App id and private key registered on it.'
  }
  return null
}

function GithubConnectionForm({
  team,
  installUrl,
  onLinked,
}: {
  team: ImpersonatableTeam
  installUrl: string | null
  onLinked: () => void
}): React.JSX.Element {
  const invalidate = useInvalidateConnections(team.id)
  const [form, setForm] = useState<ConnectionFormState>(() => emptyForm(team))
  // The API's answer to the last attempt. Any edit retires it.
  const [saveError, setSaveError] = useState<string | null>(null)
  const create = useCreateTeamConnectionApiInternalTeamsTeamIdConnectionsPost({
    mutation: {
      onSuccess: () => {
        invalidate()
        onLinked()
        setForm(emptyForm(team))
        toast.success('GitHub connected')
      },
      onError: (error: unknown) => {
        setSaveError(getApiErrorDetail(error) ?? 'The connection was not recorded')
      },
    },
  })

  const dirty = JSON.stringify(form) !== JSON.stringify(emptyForm(team))
  const validationError = dirty ? validateConnection(form) : null
  const canSubmit = !create.isPending && dirty && validationError === null
  const enterprise = isEnterpriseHost(form)

  const update = (key: keyof ConnectionFormState, value: string): void => {
    setForm((previous) => ({ ...previous, [key]: value }))
    setSaveError(null)
  }
  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault()
    if (!canSubmit) return
    setSaveError(null)
    create.mutate({
      teamId: team.id,
      data: {
        kind: 'github',
        display_name: form.display_name.trim(),
        installation_id: Number(form.installation_id.trim()),
        api_base_url: form.api_base_url.trim(),
        app_id: enterprise ? form.app_id.trim() : null,
        app_private_key: enterprise ? form.app_private_key.trim() : null,
      },
    })
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-xl space-y-4">
      {installUrl !== null && (
        <p className="text-sm text-muted-foreground">
          The customer installs the App from{' '}
          <a href={installUrl} target="_blank" rel="noreferrer" className={EXTERNAL_LINK}>
            its install page
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>{' '}
          and sends the installation id. Record it here.
        </p>
      )}
      <TextField
        id="github-display-name"
        label="Name"
        value={form.display_name}
        onChange={(value) => { update('display_name', value) }}
        placeholder="Acme GitHub"
      />
      <TextField
        id="github-installation-id"
        label="Installation id"
        value={form.installation_id}
        onChange={(value) => { update('installation_id', value) }}
        placeholder="12345678"
        hint="Find it at the end of the installation URL under the org's GitHub Apps settings."
        mono
      />
      <TextField
        id="github-api-base-url"
        label="API base URL"
        value={form.api_base_url}
        onChange={(value) => { update('api_base_url', value) }}
        placeholder={GITHUB_CLOUD_API_BASE_URL}
        hint="Keep the default for cloud GitHub, or enter https://<host>/api/v3 for a GitHub Enterprise Server instance."
        mono
      />
      {enterprise && (
        <EnterpriseAppFields
          appId={form.app_id}
          privateKey={form.app_private_key}
          onAppIdChange={(value) => { update('app_id', value) }}
          onPrivateKeyChange={(value) => { update('app_private_key', value) }}
        />
      )}
      <SettingsFormFooter dirty={dirty} error={validationError ?? saveError}>
        <Button
          type="button"
          variant="outline"
          disabled={!dirty || create.isPending}
          onClick={() => {
            setForm(emptyForm(team))
            setSaveError(null)
          }}
        >
          Discard
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          {create.isPending ? 'Connecting...' : 'Connect'}
        </Button>
      </SettingsFormFooter>
    </form>
  )
}

function EnterpriseAppFields({
  appId,
  privateKey,
  onAppIdChange,
  onPrivateKeyChange,
}: {
  appId: string
  privateKey: string
  onAppIdChange: (value: string) => void
  onPrivateKeyChange: (value: string) => void
}): React.JSX.Element {
  return (
    <>
      <TextField
        id="github-app-id"
        label="App id"
        value={appId}
        onChange={onAppIdChange}
        placeholder="777"
        hint="The App is registered on the customer's own instance."
        mono
      />
      <div className="space-y-2">
        <Label htmlFor="github-app-private-key">App private key</Label>
        <Textarea
          id="github-app-private-key"
          className="font-mono text-xs"
          rows={6}
          value={privateKey}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => { onPrivateKeyChange(e.target.value) }}
          placeholder="-----BEGIN RSA PRIVATE KEY-----"
          aria-describedby="github-app-private-key-hint"
        />
        <p id="github-app-private-key-hint" className="text-xs text-muted-foreground">
          The PEM is encrypted before the connection is written and never shown again.
        </p>
      </div>
    </>
  )
}

function TextField({
  id,
  label,
  value,
  onChange,
  placeholder,
  hint,
  mono = false,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  placeholder: string
  hint?: string
  mono?: boolean
}): React.JSX.Element {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        className={mono ? 'font-mono' : undefined}
        value={value}
        autoComplete="off"
        onChange={(e) => { onChange(e.target.value) }}
        placeholder={placeholder}
        aria-describedby={hint === undefined ? undefined : `${id}-hint`}
      />
      {hint !== undefined && (
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      )}
    </div>
  )
}
